/**
 * Pure view-model logic for the LLM lane census.
 *
 * Kept free of React — like `laneQueueModel.ts` beside it — so the wait-time,
 * traffic-split and limit rules can be tested directly.
 *
 * THE ONE RULE THAT MATTERS HERE: nothing in this file invents a number. An
 * idle lane has no oldest-pending instant (null, not "0s"), and a lane with no
 * limiter seeded in this process has no cap (that is NOT "unlimited", and it is
 * NOT zero concurrency). Both cases return null so the component can say so in
 * words rather than render a confident lie.
 */
import type { LlmLaneDepth, LlmLaneLimits } from '../../types/llmLanes'

/** Labels for the request types this build knows about. */
const TYPE_LABELS: Record<string, string> = {
  autocoder_batch_code: 'AutoCoder · slim coding',
  autocoder_batch_extract: 'AutoCoder · extract',
  autocoder_batch_classify: 'AutoCoder · classify',
  autocoder_batch_stitch: 'AutoCoder · stitch',
  themeai_generate: 'Theme AI',
}

/**
 * Label for an `llm_request.type`. An unrecognised type renders as ITSELF: an
 * operator who sees `exec_report_synthesis` competing for a lane has learned
 * something; one who sees "Unknown" has only learned that we have a bug.
 */
export const requestTypeLabel = (type: string): string => TYPE_LABELS[type] ?? type

export interface LaneTypeShare {
  type: string
  label: string
  count: number
}

/**
 * The in-flight traffic split for one lane, busiest first (ties by label so the
 * order is stable poll to poll). Empty when the lane is idle — the server only
 * sends non-zero types.
 */
export const laneTypeShares = (lane: LlmLaneDepth): LaneTypeShare[] =>
  Object.entries(lane.by_type ?? {})
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({ type, label: requestTypeLabel(type), count }))
    .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label))

/**
 * How long the oldest waiting request has been waiting, as a compact string.
 *
 * Returns null when nothing is waiting (there is no answer) or the timestamp is
 * unparseable. A clock skew that puts the instant in the future clamps to 0s
 * rather than rendering a negative age.
 */
export const formatWaitAge = (
  oldestPendingAt: string | null | undefined,
  now: number = Date.now(),
): string | null => {
  if (!oldestPendingAt) return null
  const t = Date.parse(oldestPendingAt)
  if (Number.isNaN(t)) return null

  const secs = Math.max(0, Math.round((now - t) / 1000))
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ${String(secs % 60).padStart(2, '0')}s`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ${String(mins % 60).padStart(2, '0')}m`
  return `${Math.floor(hours / 24)}d ${String(hours % 24).padStart(2, '0')}h`
}

/** Compact thousands: 500000 -> "500k", 1200000 -> "1.2M". */
const compact = (n: number): string => {
  if (n >= 1_000_000) {
    const m = n / 1_000_000
    return `${m >= 10 ? Math.round(m) : Math.round(m * 10) / 10}M`
  }
  if (n >= 1_000) {
    const k = n / 1_000
    return `${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`
  }
  return n.toLocaleString()
}

/**
 * The budget in force for a lane, in words.
 *
 * ALL ZEROS is not "no limit" and not "no concurrency": it means no limiter is
 * seeded for this lane in the API process (an unrecognised lane, or one this
 * environment has no credentials for). That returns null, and the caller says
 * "no limiter here" — never a number.
 */
export const formatLaneLimits = (limits: LlmLaneLimits | undefined): string | null => {
  if (!limits) return null
  const { tpm = 0, rpm = 0, max_concurrency: cap = 0 } = limits
  if (!tpm && !rpm && !cap) return null

  const parts: string[] = []
  if (tpm) parts.push(`${compact(tpm)} TPM`)
  if (rpm) parts.push(`${compact(rpm)} RPM`)
  if (cap) parts.push(`${cap} concurrent`)
  return parts.join(' · ')
}

/** Total unsettled work on a lane — what "depth" means. */
export const laneDepth = (lane: LlmLaneDepth): number => lane.pending + lane.running

/**
 * Whether the census has anything moving at all. Drives the "everything is
 * idle" empty state, which is a real and common answer rather than a failure.
 */
export const censusIsIdle = (lanes: LlmLaneDepth[]): boolean =>
  lanes.every(lane => laneDepth(lane) === 0)
